import { ErrorMessage } from '@hookform/error-message';
import { UseQueryResult } from '@tanstack/react-query';
import { Address, PickupLocation, PickupLocationType } from '~api/som';

import { ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { formatAddress, getAddressTemplate } from '@ingrid/address';
import {
  Banner,
  Button,
  FormItem,
  ListLayout,
  Modal,
  OptionList,
  PileBadge,
  Select,
  SelectTrigger,
  ToggleButton,
} from '@ingrid/luterkort-react';

import { Distance } from '~src/components/Distance/Distance';
import { useTranslation } from '~src/i18n/use-translation';
import { stringifyDateTimeRange } from '~src/utils/date';

import { usePickupLocationQuery } from '../queries';
import { ShipmentUpsert, useShipmentFormContext } from '../types';

const LOCATION_TYPES: Exclude<PickupLocationType, 'UNKNOWN_PICKUP_LOCATION_TYPE'>[] = [
  'LOCKER',
  'MANNED',
  'POSTOFFICE',
  'STORE',
];

interface Props {
  trigger: (query: UseQueryResult<PickupLocation[], Error>) => ReactNode;
  onSubmit: (payload: PickupLocation) => void;
  value?: Pick<ShipmentUpsert, 'location_ref'>;
}

interface PickupPointFormData {
  locationRef: string | undefined;
}

interface FilterFormData {
  locationTypes: PickupLocationType[];
  addressType: AddressType;
}

type AddressType = 'consignee' | 'delivery';

function getPickupPointLocationByReference(
  locationRef: string | undefined,
  availablePickupPoints: PickupLocation[] = []
) {
  return availablePickupPoints.find(location => location.location_ref === locationRef);
}

function formatInlineAddress(address?: Address) {
  if (!address?.country) {
    return undefined;
  }

  return formatAddress(
    [
      ['address_line_1', address.address_lines?.[0]],
      ['city', address.city],
    ],
    {
      template: getAddressTemplate(address.country),
      multiline: false,
    }
  );
}

function usePickupLocationForm(value: Props['value']) {
  return useForm<PickupPointFormData>({
    defaultValues: {
      locationRef: value?.location_ref,
    },
  });
}

function useFilterForm() {
  return useForm<FilterFormData>({
    defaultValues: {
      locationTypes: ['LOCKER', 'MANNED'],
      addressType: 'consignee',
    },
  });
}

function PickupPointItem({ location }: { location: PickupLocation }) {
  const { t } = useTranslation();

  return (
    <OptionList.Item key={location.location_ref} value={location.location_ref!}>
      <ListLayout.Stack>
        <ListLayout.SubRow gap="sm">
          <ListLayout.PrimaryText>{location.name}</ListLayout.PrimaryText>
          {location.location_type && location.location_type !== 'UNKNOWN_PICKUP_LOCATION_TYPE' && (
            <PileBadge>{t(`shipments.create.pickupLocationType.${location.location_type}`)}</PileBadge>
          )}
          {location.distances?.walk_distance && (
            <PileBadge>
              <Distance distance={location.distances?.walk_distance / 1000} unit="kilometer" />
            </PileBadge>
          )}
        </ListLayout.SubRow>
        <ListLayout.SecondaryText>{formatInlineAddress(location.address)}</ListLayout.SecondaryText>
      </ListLayout.Stack>

      <OptionList.ItemIndicator />
    </OptionList.Item>
  );
}

export function CarrierProductPickupPoint({ trigger, value, onSubmit }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const shipment = useShipmentFormContext();
  const form = usePickupLocationForm(value);
  const filter = useFilterForm();

  const query = usePickupLocationQuery({
    address_to:
      filter.watch('addressType') === 'delivery'
        ? shipment.watch('contact_to.address')
        : shipment.watch('customer_info.contact.address'),
    pickup_time: stringifyDateTimeRange({
      start: shipment.watch('delivery_time.start')!,
      end: shipment.watch('delivery_time.end')!,
    }),
    shipping_method: shipment.watch('shipping_method.id'),
    address_from: shipment.watch('contact_from.address'),
    location_type_filters: filter.watch('locationTypes'),
  });

  const { t } = useTranslation();

  function handleReset() {
    form.setValue('locationRef', value?.location_ref);
    form.clearErrors();
  }

  function handleSubmit(values: PickupPointFormData) {
    const location = getPickupPointLocationByReference(values.locationRef, query.data);

    /**
     * If this happens, it's probably because you selected a pickup point that is not longer available.
     * Most likely because you changed address, or the location type filters after selecting a location.
     *
     * If that's the case, we just handle it as a regular required validation error.
     */
    if (!location) {
      form.setError('locationRef', { message: t('common.fieldRequired', { field: t('common.pickupPoint') }) });
      return;
    }

    onSubmit(location);
    setIsOpen(false);
  }

  const consignee = shipment.watch('customer_info.contact.address');
  const delivery = shipment.watch('contact_to.address');

  const addresses = (
    [
      ['consignee', formatInlineAddress(consignee)] as const,
      ['delivery', formatInlineAddress(delivery)] as const,
    ] satisfies [AddressType, string | undefined][]
  )
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, a]) => !!a);

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={o => {
        handleReset();
        setIsOpen(o);
      }}
    >
      <Modal.Trigger>{trigger(query)}</Modal.Trigger>

      <Modal.Content>
        <Modal.Header title={t('shipments.pickupPoint.selectPickupPoint')} />

        <Modal.Body>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            style={{ display: 'grid', gap: '16px' }}
            id="form-pickup-point"
          >
            {query.isError && <Banner variant="critical" title={t('errors.generic')} subtitle={query.error?.message} />}
            {query.isSuccess && !query.data.length && (
              <Banner
                subtitle={t('shipments.create.pickupPointNoOptions.text')}
                title={t('shipments.create.pickupPointNoOptions.title')}
                variant="warning"
              />
            )}

            <FormItem.Root>
              <FormItem.Label>{t('common.locationTypes')}</FormItem.Label>
              <FormItem.Control>
                <div style={{ display: 'flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                  {LOCATION_TYPES.map(type => (
                    <ToggleButton
                      pressed={filter.watch('locationTypes').includes(type)}
                      onPressedChange={pressed => {
                        const prev = filter.getValues('locationTypes');

                        filter.setValue(
                          'locationTypes',
                          !pressed ? [...prev.filter(p => p !== type)] : [...prev, type]
                        );
                      }}
                      key={type}
                    >
                      {t(`shipments.create.pickupLocationType.${type}`)}
                    </ToggleButton>
                  ))}
                </div>
              </FormItem.Control>
              <FormItem.HintText>{t('shipments.create.pickupLocationTypeHint')}</FormItem.HintText>
            </FormItem.Root>

            <FormItem.Root>
              <FormItem.Label>{t('common.address')}</FormItem.Label>
              <FormItem.Control>
                <Select.Root
                  value={filter.watch('addressType') ? [filter.watch('addressType')] : undefined}
                  onValueChange={([key]) => filter.setValue('addressType', key as AddressType)}
                >
                  <Select.Trigger>
                    <SelectTrigger.Button>
                      <SelectTrigger.Placeholder>{t('common.selectAddress')}</SelectTrigger.Placeholder>

                      {addresses.map(([key, address]) => (
                        <SelectTrigger.Value key={key} value={key}>
                          {address}
                        </SelectTrigger.Value>
                      ))}
                      <SelectTrigger.Icon />
                    </SelectTrigger.Button>
                  </Select.Trigger>

                  <Select.Content css={{ width: '608px' }}>
                    <OptionList.Viewport>
                      {addresses.map(([key, address]) => (
                        <OptionList.Item key={key} value={key}>
                          <ListLayout.Stack>
                            <ListLayout.SubRow gap="sm">
                              <ListLayout.PrimaryText>{address}</ListLayout.PrimaryText>
                              <PileBadge>{t(`common.${key}`)}</PileBadge>
                            </ListLayout.SubRow>
                          </ListLayout.Stack>

                          <OptionList.ItemIndicator />
                        </OptionList.Item>
                      ))}
                    </OptionList.Viewport>
                  </Select.Content>
                </Select.Root>
              </FormItem.Control>
              <FormItem.HintText>{t('shipments.create.pickupPointAddressHint')}</FormItem.HintText>
            </FormItem.Root>

            <FormItem.Root>
              <FormItem.Label>{t('common.pickupPoint')}</FormItem.Label>

              <FormItem.Control>
                <Controller
                  rules={{
                    required: { value: true, message: t('common.fieldRequired', { field: t('common.pickupPoint') }) },
                  }}
                  control={form.control}
                  name="locationRef"
                  render={({ field, fieldState }) => (
                    <Select.Root
                      value={field.value ? [field.value] : undefined}
                      onValueChange={([key]) => field.onChange(key)}
                    >
                      <Select.Trigger data-invalid={fieldState.invalid}>
                        <SelectTrigger.Button data-testid="pickup-point-select">
                          <SelectTrigger.Placeholder>
                            {t('shipments.pickupPoint.selectPickupPoint')}
                          </SelectTrigger.Placeholder>

                          {query.data?.map(location => (
                            <SelectTrigger.Value key={location.location_ref} value={location.location_ref!}>
                              {location.name}
                            </SelectTrigger.Value>
                          ))}
                          <SelectTrigger.Icon />
                        </SelectTrigger.Button>
                      </Select.Trigger>

                      <Select.Content css={{ width: '608px' }}>
                        <OptionList.Viewport>
                          {query.data?.map(location => (
                            <PickupPointItem location={location} key={location.location_ref} />
                          ))}
                        </OptionList.Viewport>
                      </Select.Content>
                    </Select.Root>
                  )}
                />
              </FormItem.Control>

              <ErrorMessage as={FormItem.ErrorText} errors={form.formState.errors} name="locationRef" />
            </FormItem.Root>
          </form>
        </Modal.Body>

        <Modal.Footer>
          <Modal.CloseButton>
            <Button variant="secondary">{t('common.cancel')}</Button>
          </Modal.CloseButton>

          <Button form="form-pickup-point" type="submit">
            {t('common.save')}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
